import { RefObject, useCallback, useEffect, useState } from 'react';

import measurePlacement, { FALLBACK_PLACEMENT, MeasuredPlacement } from '../../floating/measurePlacement';
import type { ParsedPlacement } from '../../floating/parsePlacement';
import useDelayTimer from '../../floating/useDelayTimer';
import usePointerType from '../../floating/usePointerType';

type UseTooltipStateParams = {
  isDisabled: boolean;
  isTouchMedia: boolean;
  isTapEnabled: boolean;
  delayHide: number;
  placement: ParsedPlacement;
  triggerRef: RefObject<HTMLElement | null>;
};

const useTooltipState = ({
  isDisabled,
  isTouchMedia,
  isTapEnabled,
  delayHide,
  placement,
  triggerRef,
}: UseTooltipStateParams) => {
  const [isOpened, setIsOpened] = useState(false);
  const [measured, setMeasured] = useState<MeasuredPlacement>(FALLBACK_PLACEMENT);
  const { isTouch, isTouchNow, handlePointerType, handleFocus } = usePointerType(isTouchMedia);
  const { clear: clearHideTimer, schedule: scheduleHide } = useDelayTimer();

  const isOpen = isOpened && !isDisabled;

  const close = useCallback(() => {
    clearHideTimer();
    setIsOpened(false);
  }, [clearHideTimer]);

  useEffect(() => {
    if (isDisabled) {
      close();
    }
  }, [isDisabled, close]);

  const open = () => {
    clearHideTimer();

    if (triggerRef.current) {
      setMeasured(measurePlacement(triggerRef.current, placement));
    }

    setIsOpened(true);
  };

  const closeWithDelay = () => {
    if (delayHide <= 0) {
      close();

      return;
    }

    scheduleHide(close, delayHide);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (isTouchNow() || isDisabled) {
      return;
    }

    if (nextOpen) {
      open();
    } else {
      closeWithDelay();
    }
  };

  const handleTriggerClick = () => {
    if (!isTouchNow() || !isTapEnabled || isDisabled) {
      return;
    }

    if (isOpened) {
      close();
    } else {
      open();
    }
  };

  return {
    isOpen,
    isTouch,
    side: placement.side ?? measured.side,
    alignOffset: measured.alignOffset,
    close,
    handlePointerType,
    handleFocus,
    handleOpenChange,
    handleTriggerClick,
  };
};

export default useTooltipState;
